import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { useConfig } from '@entities/config/api';
import { ROUTES } from '@shared/config/routes';
import { money } from '@shared/lib/money';
import { percentFromBps } from '@shared/lib/percent';
import { Skeleton } from '@shared/ui';

import { LegalArticle } from './LegalArticle';

/**
 * AGB — §§ 305 ff. BGB.
 *
 * Три вещи, которые этот текст обязан делать одновременно:
 *
 * 1. **включаться в договор** — явная ссылка и возможность ознакомиться до
 *    заказа (§ 305 Abs. 2 BGB). Отсюда ссылка на экране оплаты и адрес без
 *    входа;
 * 2. **не обещать того, чего нет** (MVP_PRD §2). Раздел «Was CarViQ.DE nicht
 *    tut» здесь не осторожность юриста, а перечень границ продукта: обещание
 *    проверки VIN, документов или добросовестности продавца было бы
 *    одновременно нарушением § 5a UWG и внутреннего запрета;
 * 3. **называть оба платежа** — 19,90 € и вознаграждение по результату
 *    (MVP_PRD §42). Умолчание о втором платеже превращает честную модель в
 *    обман.
 *
 * **Цена не зашита в текст.** Она приходит из `GET /config`: хардкод цены в
 * интерфейсе и в локалях запрещён (MVP_TECH_RFC §25), и AGB не исключение —
 * иначе `SERVICE_PRICE_CENTS` и договор разойдутся молча.
 */
export function TermsPage() {
  const { t } = useTranslation();
  const { data, isPending } = useConfig();

  if (isPending || !data) {
    return (
      <LegalArticle title="Allgemeine Geschäftsbedingungen">
        <Skeleton height="24rem" radius="var(--radius-card)" />
      </LegalArticle>
    );
  }

  const operator = data.legal.operator;
  const price = money(data.payments.price_cents, data.payments.currency, 'de');
  const flatFee = money(data.success_fee.flat_cents, data.payments.currency, 'de');
  // AGB — немецкий текст: ставка по немецкой локали, «25,5», а не «25.5».
  const rate = percentFromBps(data.success_fee.rate_bps, 'de');
  const withdrawalDays = data.legal.withdrawal_period_days;

  return (
    <LegalArticle
      title="Allgemeine Geschäftsbedingungen"
      version={data.legal.document_versions.terms}
      lead={<p>{t('legal.terms.lead')}</p>}
    >
      <h2>§ 1 Anbieter, Geltungsbereich und Vertragssprache</h2>
      <p>
        Anbieter der CarViQ.DE-Dienstleistung ist die {operator.legal_name}, {operator.street},{' '}
        {operator.postal_code} {operator.city}, {operator.country} (nachfolgend „CarViQ.DE"). Diese
        Bedingungen gelten für alle Verträge über die CarViQ.DE-Dienstleistung, die Verbraucherinnen
        und Verbraucher im Sinne des § 13 BGB über die Website abschließen.
      </p>
      <p>
        Vertragssprache ist Deutsch. Übersetzungen dieser Bedingungen dienen ausschließlich dem
        Verständnis; maßgeblich ist die deutsche Fassung. Abweichende Bedingungen der Kundin oder
        des Kunden werden nicht Vertragsbestandteil, es sei denn, CarViQ.DE stimmt ihrer Geltung
        ausdrücklich zu.
      </p>

      <h2>§ 2 Gegenstand der Leistung</h2>
      <p>CarViQ.DE erbringt eine Dienstleistung, die aus folgenden Bestandteilen besteht:</p>
      <ul>
        <li>
          Erfassung der Suchkriterien im Dialog mit dem KI-Assistenten Karl und Umwandlung in
          strukturierte Kriterien;
        </li>
        <li>
          Auswertung des CarViQ.DE-Katalogs nach einem festen, nachvollziehbaren Verfahren und
          Zusammenstellung einer Arbeitsmenge;
        </li>
        <li>
          Auswahl von bis zu vier Fahrzeugen mit Begründung sowie Fragen an die betreffenden
          Autohäuser; Antworten werden wörtlich zitiert und als Angaben des Autohauses
          gekennzeichnet;
        </li>
        <li>Vergleich der Fahrzeuge und eine begründete Empfehlung;</li>
        <li>
          auf ausdrücklichen Auftrag: vertiefte Fragen und Preisverhandlung mit dem Autohaus für die
          von der Kundin oder dem Kunden ausgewählten Fahrzeuge, im Rahmen eines von ihr oder ihm
          erteilten Mandats;
        </li>
        <li>
          Begleitung bis zum Termin beim Autohaus, einschließlich einer Checkliste und eines
          mechanischen Abgleichs der Felder eines selbst hochgeladenen Dokuments mit den zuvor
          festgehaltenen Angaben.
        </li>
      </ul>
      <p>
        Karl ist ein KI-System im Sinne der Verordnung (EU) 2024/1689 und wird als solches
        gekennzeichnet. Nachrichten an Autohäuser werden als von einer künstlichen Intelligenz
        erstellt gekennzeichnet.
      </p>

      <h2>§ 3 Was CarViQ.DE nicht tut</h2>
      <p>
        Der Leistungsumfang ist abschließend beschrieben. Insbesondere schuldet CarViQ.DE nicht und
        sagt nicht zu:
      </p>
      <ul>
        <li>
          eine Prüfung von Fahrzeug-Identifizierungsnummer, Unterlagen, Service-, Unfall- oder
          Halterhistorie oder des technischen Zustands. CarViQ.DE <strong>fragt</strong> beim
          Autohaus nach und gibt die Antwort wörtlich wieder; eine eigene Bestätigung oder
          Widerlegung erfolgt nicht;
        </li>
        <li>eine Auswertung von Fotos, Videos oder Anhängen;</li>
        <li>eine Prognose künftiger Reparaturen oder Betriebskosten;</li>
        <li>
          eine Bewertung der Zuverlässigkeit, Seriosität oder Vertrauenswürdigkeit eines Autohauses.
          Angaben wie Unternehmensalter, eigene Werkstatt oder eine Google-Bewertung werden
          ausschließlich als Tatsache mit Quelle und Stichtag wiedergegeben und nicht zu einem
          Urteil verdichtet;
        </li>
        <li>
          eine Rechtsdienstleistung im Sinne des § 2 RDG, insbesondere keine Prüfung, Erstellung
          oder Änderung von Verträgen und keine Rechtsberatung. Der Dokumentenabgleich nach § 2 ist
          ein mechanischer Feldvergleich ohne rechtliche Bewertung;
        </li>
        <li>eine Prüfung der Echtheit hochgeladener Dokumente;</li>
        <li>eine Suche außerhalb des CarViQ.DE-Katalogs.</li>
      </ul>

      <h2>§ 4 Zustandekommen des Vertrags</h2>
      <p>
        Die Darstellung der Leistung auf der Website ist kein bindendes Angebot. Mit dem Klick auf
        „Zahlungspflichtig bestellen" gibt die Kundin oder der Kunde ein verbindliches Angebot ab.
        Unmittelbar vor der Schaltfläche werden die wesentlichen Merkmale der Leistung, der
        Gesamtpreis einschließlich Umsatzsteuer und die Bedingungen der Erfolgsvergütung angezeigt.
        Der Vertrag kommt mit der Bestätigung der Bestellung durch CarViQ.DE zustande.
      </p>
      <p>
        Eingabefehler können vor dem Absenden der Bestellung durch Ändern der Angaben im
        Bestellvorgang oder durch Verlassen der Seite korrigiert werden; die Präzisierung der
        Suchanfrage ist jederzeit und kostenfrei möglich. CarViQ.DE bestätigt den Vertrag
        unverzüglich per E-Mail auf einem dauerhaften Datenträger; die Bestätigung enthält diese
        Bedingungen, die Widerrufsbelehrung und das Muster-Widerrufsformular.
      </p>

      <h2>§ 5 Preis, Umsatzsteuer und Zahlung</h2>
      <p>
        Die Vergütung für die Bearbeitung eines Auftrags beträgt einmalig {price} einschließlich
        Umsatzsteuer. Weitere Kosten für den Vertragsschluss entstehen nicht; Kosten für die Nutzung
        des Internetzugangs trägt die Kundin oder der Kunde selbst.
      </p>
      <p>
        Akzeptierte Zahlungsmittel werden im Bestellvorgang angezeigt. Lieferbeschränkungen bestehen
        nicht; die Leistung wird online erbracht.
      </p>

      <h2>§ 6 Erfolgsvergütung</h2>
      <p>
        Zusätzlich zur Vergütung nach § 5 kann eine Erfolgsvergütung anfallen. Sie entsteht erst,
        wenn die Kundin oder der Kunde den Kauf eines Fahrzeugs bestätigt, für das CarViQ.DE
        verhandelt hat.
      </p>
      <ul>
        <li>
          <strong>Preisnachlass:</strong> {rate} % des tatsächlich erzielten Preisnachlasses
          einschließlich Umsatzsteuer. Bemessungsgrundlage ist die Differenz zwischen dem
          festgehaltenen Ausgangspreis und dem Preis des angenommenen Angebots; beide Werte werden
          mit ihrer Grundlage dokumentiert.
        </li>
        <li>
          <strong>Wirtschaftlicher Vorteil ohne Preisnachlass:</strong> {flatFee} einschließlich
          Umsatzsteuer, wenn statt eines Preisnachlasses eine zusätzliche Leistung gewährt wurde und
          diese durch eine wörtliche Angabe des Autohauses belegt ist. Der Wert der zusätzlichen
          Leistung wird nicht geschätzt.
        </li>
        <li>
          Beide Beträge fallen nie nebeneinander an. Ohne Preisnachlass und ohne belegten
          zusätzlichen Vorteil beträgt die Erfolgsvergütung 0 €. Kommt kein Kauf zustande, fällt
          keine Erfolgsvergütung an.
        </li>
      </ul>
      <p>
        Die Erfolgsvergütung hat keinen Einfluss auf die Auswahl der Fahrzeuge, ihre Bewertung oder
        die Empfehlung. Autohäusern gegenüber wird die Erfolgsvergütung nicht erwähnt.
      </p>

      <h2>§ 7 Mitwirkung der Kundin oder des Kunden</h2>
      <p>
        CarViQ.DE kann nur mit den Angaben arbeiten, die im Dialog gemacht werden. Für die
        Richtigkeit dieser Angaben und für die Entscheidung, ein Fahrzeug zu kaufen, ist die Kundin
        oder der Kunde verantwortlich. Ein Kaufvertrag über ein Fahrzeug kommt ausschließlich
        zwischen ihr oder ihm und dem Autohaus zustande; CarViQ.DE ist daran nicht beteiligt, nimmt
        keine Zahlung für das Fahrzeug entgegen und reserviert keine Fahrzeuge.
      </p>

      <h2>§ 8 Widerrufsrecht</h2>
      <p>
        Verbraucherinnen und Verbrauchern steht ein Widerrufsrecht von {withdrawalDays} Tagen zu.
        Einzelheiten und das Muster-Widerrufsformular enthält die{' '}
        <Link to={ROUTES.withdrawal}>Widerrufsbelehrung</Link>. Der Widerruf kann jederzeit über die
        Schaltfläche <Link to={ROUTES.withdrawalForm}>„Vertrag widerrufen"</Link> erklärt werden.
      </p>
      <p>
        Verlangt die Kundin oder der Kunde ausdrücklich den Beginn der Leistung vor Ablauf der
        Widerrufsfrist, erlischt das Widerrufsrecht mit vollständiger Erbringung der Leistung. Wird
        vorher widerrufen, ist Wertersatz für den bereits erbrachten Teil zu leisten.
      </p>

      <h2>§ 9 Verfügbarkeit und Haftung</h2>
      <p>
        CarViQ.DE erbringt die Leistung mit der Sorgfalt eines ordentlichen Kaufmanns, schuldet aber
        keinen bestimmten Verhandlungserfolg und keine ununterbrochene Verfügbarkeit der Website.
      </p>
      <p>
        CarViQ.DE haftet unbeschränkt bei Vorsatz und grober Fahrlässigkeit, bei Verletzung von
        Leben, Körper oder Gesundheit sowie nach dem Produkthaftungsgesetz. Bei einfacher
        Fahrlässigkeit haftet CarViQ.DE nur bei Verletzung einer Pflicht, deren Erfüllung die
        ordnungsgemäße Durchführung des Vertrags überhaupt erst ermöglicht und auf deren Einhaltung
        vertraut werden darf, und begrenzt auf den vertragstypischen, vorhersehbaren Schaden. Eine
        weitergehende Haftung ist ausgeschlossen. Für Angaben der Autohäuser haftet CarViQ.DE nicht:
        sie werden ausdrücklich als deren Angaben wiedergegeben und nicht überprüft.
      </p>

      <h2>§ 10 Vertragsdauer und Kündigung</h2>
      <p>
        Der Vertrag betrifft einen einzelnen Auftrag und endet mit dessen Abschluss. Ein
        Dauerschuldverhältnis, ein Abonnement oder eine automatische Verlängerung bestehen nicht.
      </p>

      <h2>§ 11 Datenschutz</h2>
      <p>
        Wie personenbezogene Daten verarbeitet werden, beschreibt die{' '}
        <Link to={ROUTES.privacy}>Datenschutzerklärung</Link>. An Autohäuser werden nur die Daten
        übermittelt, die für die konkrete Anfrage erforderlich sind.
      </p>

      <h2>§ 12 Barrierefreiheit</h2>
      <p>
        Angaben dazu, wie CarViQ.DE die Anforderungen an die Barrierefreiheit erfüllt, sind unter{' '}
        <Link to={ROUTES.accessibility}>Erklärung zur Barrierefreiheit</Link> abrufbar.
      </p>

      <h2>§ 13 Beschwerden und Streitbeilegung</h2>
      <p>
        Beschwerden nimmt CarViQ.DE unter <a href={`mailto:${operator.email}`}>{operator.email}</a>{' '}
        entgegen und bearbeitet sie unverzüglich. Die {operator.legal_name} ist weder verpflichtet
        noch bereit, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle
        teilzunehmen, soweit keine gesetzliche Verpflichtung besteht.
      </p>

      <h2>§ 14 Schlussbestimmungen</h2>
      <p>
        Es gilt das Recht der Republik Litauen unter Ausschluss des UN-Kaufrechts. Diese Rechtswahl
        lässt die zwingenden Verbraucherschutzvorschriften des Staates unberührt, in dem die
        Verbraucherin oder der Verbraucher ihren oder seinen gewöhnlichen Aufenthalt hat (Art. 6
        Abs. 2 Rom-I-VO); für Verbraucherinnen und Verbraucher mit gewöhnlichem Aufenthalt in
        Deutschland gelten daher die zwingenden Vorschriften des deutschen Rechts.
      </p>
      <p>
        Sollte eine Bestimmung unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen
        unberührt. CarViQ.DE kann diese Bedingungen für künftige Verträge ändern; für bereits
        geschlossene Verträge gilt die Fassung, die bei Vertragsschluss angezeigt wurde. Die jeweils
        geltende Fassung ist an dieser Stelle mit Datum abrufbar.
      </p>
      <p>{t('legal.terms.consumerRights')}</p>
    </LegalArticle>
  );
}
